import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import StoryCoverCard from "./StoryCoverCard";

// Small, scrollable Reader-facing dialog. The teaser is author-approved metadata.
export default function StoryTeaserDialog({ story, authorName, language = "en", onRead, onClose }) {
  const closeRef = useRef(null);
  const fr = language === "fr";
  const title = String(story?.title || (fr ? "Livre" : "Story"));
  const content = typeof story?.storyTeaser === "string" ? story.storyTeaser.trim() : "";

  useEffect(() => {
    if (!content) return undefined;
    const priorFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const handleKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKey);
      if (priorFocus && typeof priorFocus.focus === "function") priorFocus.focus();
    };
  }, [onClose, content]);

  if (!content) return null;

  const popup = (
    <div
      className="ns-story-teaser-backdrop"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose?.(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 11000,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 18, background: "rgba(6, 27, 23, 0.70)",
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="ns-story-teaser-title"
        aria-describedby="ns-story-teaser-body"
        className="ns-story-teaser-dialog"
        style={{
          width: "min(100%, 600px)", maxHeight: "min(82vh, 720px)",
          overflowY: "auto", overscrollBehavior: "contain",
          background: "#fffdf7", color: "#0f172a",
          border: "1px solid rgba(214,178,94,0.6)", borderRadius: 16,
          padding: "22px clamp(16px, 3vw, 28px)",
          boxShadow: "0 28px 80px rgba(0,0,0,.32)",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14 }}>
          <div>
            <div style={{ letterSpacing: "0.12em", textTransform: "uppercase", fontSize: 11, fontWeight: 800, color: "#0b703d" }}>
              {fr ? "Un avant-goût de l’histoire" : "Story Teaser"}
            </div>
            <h2 id="ns-story-teaser-title" style={{ fontFamily: "Georgia, serif", fontSize: 23, margin: "8px 0 2px" }}>{title}</h2>
            <p style={{ margin: "4px 0 14px", fontSize: 13, color: "#475569" }}>
              {fr ? "Par" : "By"} {authorName || (fr ? "Auteur" : "Author")}
            </p>
          </div>
          <button
            ref={closeRef} type="button" className="ns-button ns-button-outline"
            onClick={onClose} aria-label={fr ? "Fermer l’accroche" : "Close Story Teaser"}
          >{fr ? "Fermer" : "Close"}</button>
        </div>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div className="storyline-cover-slot" style={{ width: 128, flex: "0 0 128px" }}>
            <StoryCoverCard story={story} authorName={authorName || ""} preferWorkingCover />
          </div>
          <p
            id="ns-story-teaser-body"
            style={{ flex: "1 1 250px", margin: 0, whiteSpace: "pre-wrap", fontSize: 15, lineHeight: 1.75 }}
          >{content}</p>
        </div>
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 26 }}>
          <button type="button" className="ns-button ns-button-outline" onClick={onClose}>
            {fr ? "Fermer" : "Close"}
          </button>
          <button type="button" className="ns-button ns-button-primary" onClick={onRead}>
            {fr ? "Commencer la lecture" : "Start Reading"}
          </button>
        </div>
      </section>
    </div>
  );
  return createPortal(popup, document.body);
}
